// Mock data for ESGine redesign

export const navItems = [
  { label: 'Platform', href: '#what' },
  { label: 'Why it matters', href: '#why' },
  { label: 'ESG-as-Code', href: '#code' },
  { label: 'Industries', href: '#industries' },
  { label: 'Workflow', href: '#workflow' },
  { label: 'Engagement', href: '#engagement' },
  { label: 'Contact', href: '#contact' },
];

export const heroSteps = [
  {
    n: '01',
    title: 'Upload your ESG report',
    desc: 'PDF, DOCX, or JSON. Add multiple documents to build your evidence pool across a single compliance check.',
  },
  {
    n: '02',
    title: 'Select frameworks and GHG scopes',
    desc: 'Choose which regulatory frameworks apply. Select Scope 1, 2, and 3 applicability. Non-applicable rules return N/A and are excluded from your score.',
  },
  {
    n: '03',
    title: 'ESG-as-Code® evaluates',
    desc: 'AI reads the document. ESG-as-Code® applies deterministic rules. Every result is Met, Not Met, or N/A. Each is mapped to a specific regulatory clause.',
  },
  {
    n: '04',
    title: 'Download audit-ready report',
    desc: 'Generate a PDF report with clause-traceable results, remediation recommendations, and a permanent compliance history.',
  },
];

export const sampleResults = [
  {
    status: 'MET',
    code: 'CSRD_GOV_1',
    title: 'Board Sustainability Oversight',
    clause: 'Clause: ESRS 2 GOV-1',
    color: 'emerald',
  },
  {
    status: 'NOT MET',
    code: 'CSRD_STRAT_1',
    title: 'Double Materiality Assessment',
    clause: 'Clause: ESRS 2 IRO-1',
    color: 'rose',
  },
  {
    status: 'N/A',
    code: 'CSRD_E1_3',
    title: 'Scope 3 GHG Emissions',
    clause: 'Scope not applicable to this entity',
    color: 'slate',
  },
];

export const whatWeDo = [
  {
    title: 'Clause-by-clause compliance',
    desc: 'Every rule maps directly to a specific clause in a specific regulation. Every outcome is traceable, auditable, and defensible. Not a benchmark. The law.',
  },
  {
    title: 'AI reads. ESG-as-Code® decides.',
    desc: 'AI extracts structured disclosure data from your documents. ESG-as-Code® evaluates every applicable rule deterministically. Same document, same result, every time. The two never swap roles.',
  },
  {
    title: 'Auditable and defensible',
    desc: 'Every outcome is traceable to its regulatory source. Every failed rule generates a specific remediation recommendation. Every result is exportable for boards, auditors, and regulators.',
  },
];

export const whyItMatters = [
  {
    title: 'Reduce regulatory risk',
    desc: 'Identify gaps, missing disclosures, and weak evidence before filings reach regulators, exchanges, or assurance providers.',
  },
  {
    title: 'Strengthen internal controls',
    desc: 'Embed ESG-as-Code® into your reporting workflow as a repeatable, deterministic control aligned with governance, risk, and compliance functions.',
  },
  {
    title: 'Support assurance and audit',
    desc: 'Provide a clear audit trail of rules applied, issues found, and actions taken. Every outcome is traceable to its regulatory source.',
  },
  {
    title: 'Protect brand and leadership',
    desc: 'Help boards, CFOs, General Counsel, and Chief Sustainability Officers sign disclosures with confidence. Every claim backed by a specific regulatory clause.',
  },
];

export const frameworks = [
  {
    code: 'CSRD',
    title: 'CSRD',
    rules: 22,
    desc: 'The Corporate Sustainability Reporting Directive is the new EU directive that takes effect for large and listed companies operating in the EU. It aims to drive accountability and transparency while promoting sustainable practices and investment.',
    region: 'European Union',
  },
  {
    code: 'SEC',
    title: 'SEC Climate',
    rules: 8,
    desc: 'The Securities and Exchange Commission (SEC) mandates that public companies standardise and enhance the reporting of climate-related risks and their financial impacts. Its objective is to provide investors with consistent, comparable, and reliable information necessary for making informed investment decisions. This reporting requirement applies only to SEC-registered companies.',
    region: 'United States',
  },
  {
    code: 'SB253',
    title: 'CARB SB 253',
    rules: 6,
    desc: 'The Climate Corporate Data Accountability Act (SB-253) applies to U.S.-based entities doing business in California with an annual revenue of over $1 billion. Companies are required to disclose their Scope 1, 2, and 3 GHG emissions annually.',
    region: 'California',
  },
  {
    code: 'SB261',
    title: 'CARB SB 261',
    rules: 12,
    desc: 'The Climate Corporate Data Accountability Act (SB-261) applies to U.S.-based entities doing business in California with revenues exceeding $500 million. Entities are required to disclose climate-related financial risks biannually.',
    region: 'California',
  },
  {
    code: 'ISSB',
    title: 'ISSB / IFRS S1 + S2',
    rules: 18,
    desc: 'An implementation guide to ISSB standards IFRS S1 and S2, covering global sustainability and climate-related disclosure requirements. S1 addresses the general requirements for the Disclosure of Sustainability-related Financial Information and S2 focuses on Climate-related Disclosures.',
    region: 'Global',
  },
  {
    code: 'FCA',
    title: 'UK FCA',
    rules: 14,
    desc: 'United Kingdom FCA Sustainability Disclosure Requirements. It covers financial firms and listed companies in the UK. It comprises the UK SRS S1 (general sustainability-related financial disclosures) and UK SRS S2 (climate-specific disclosures).',
    region: 'United Kingdom',
  },
  {
    code: 'SFDR',
    title: 'SFDR',
    rules: 12,
    desc: 'European Union Sustainable Finance Disclosure Regulation, covering EU fund managers and investment advisers required to disclose how they integrate sustainability risks into investment decisions and classify their financial products.',
    region: 'European Union',
  },
];

export const operationSteps = [
  {
    step: 'Step 01',
    title: 'Ingest disclosure package',
    desc: 'Upload draft ESG and climate disclosures, data tables, narrative sections and supporting files intended for filing.',
  },
  {
    step: 'Step 02',
    title: 'Extract and structure evidence',
    desc: 'The system structures text, figures and references so compliance checks can be run consistently across the package.',
  },
  {
    step: 'Step 03',
    title: 'Run deterministic checks',
    desc: 'ESG-as-Code® evaluates completeness, consistency, comparability and disclosure risk using executable regulatory logic.',
  },
  {
    step: 'Step 04',
    title: 'Return findings and remediation',
    desc: 'Teams receive flagged issues, traceable references and a cleaner path to regulator-ready submission packages.',
  },
];

export const industries = [
  {
    title: 'Financial Services',
    desc: 'Banks, asset managers, and insurers preparing SFDR, CSRD, FCA, and ISSB disclosures simultaneously.',
    tags: ['SFDR', 'CSRD', 'FCA', 'ISSB'],
  },
  {
    title: 'Energy and Utilities',
    desc: 'Emissions-intensive sectors navigating CARB SB253, SEC Climate, and CSRD obligations across multiple jurisdictions.',
    tags: ['SB253', 'SEC', 'CSRD'],
  },
  {
    title: 'Manufacturing & Supply Chain',
    desc: 'Companies managing complex emissions data, supplier disclosures, and cross-jurisdictional ESG reporting obligations.',
    tags: ['ISSB', 'CSRD', 'SEC'],
  },
  {
    title: 'Listed & Late-stage Private',
    desc: 'Organizations where ESG and climate disclosures are scrutinized by markets, regulators, and rating agencies.',
    tags: ['ISSB', 'SEC', 'CSRD'],
  },
];

export const engagementModels = [
  {
    n: '01',
    title: 'Pre-submission review pilot',
    desc: 'Time-bound pilot focused on one or more upcoming ESG/Climate filings. Includes access to ESGine®, ESG-as-Code® checks in scope, and support for interpreting findings.',
    note: 'Ideal for organizations running their first structured pre-submission review.',
  },
  {
    n: '02',
    title: 'Annual access',
    desc: 'Ongoing access aligned to your reporting calendar, covering multiple filings and coordination between ESG, finance, legal, risk and internal audit teams.',
    note: 'Suitable for organizations with recurring ESG/Climate disclosure obligations.',
  },
  {
    n: '03',
    title: 'Embedded & integration',
    desc: 'ESG-as-Code® embedded into internal controls, reporting tools or assurance workflows, with dedicated rule libraries, governance and integration support.',
    note: 'Designed for larger organizations and partners seeking deeper integration.',
  },
];

export const trustPillars = [
  {
    title: 'Deterministic compliance logic',
    desc: 'Compliance outcomes are driven by rule-based ESG-as-Code® checks, helping teams understand why a finding appears.',
  },
  {
    title: 'Evidence-backed findings',
    desc: 'Findings can be traced to disclosure sections, supporting materials and the specific regulatory logic applied.',
  },
  {
    title: 'Exportable audit trail',
    desc: 'Generate outputs that support board review, internal sign-off, assurance preparation and regulator-facing documentation.',
  },
];

export const stats = [
  { value: '7', label: 'Live frameworks' },
  { value: '90+', label: 'Deterministic rules' },
  { value: '3', label: 'Jurisdictions covered' },
  { value: '100%', label: 'Clause-traceable outcomes' },
];

export const sampleEvaluation = {
  framework: 'CSRD',
  rulesEvaluated: 22,
  met: 14,
  notMet: 5,
  na: 3,
};
